import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Plus, Save, Trash2, Grid3X3, Home, Settings, CheckCircle, GripVertical } from 'lucide-react';
import { JobberService } from '../services/jobberService';

const SettingsView = ({ 
  windowTypes, 
  setWindowTypes, 
  customerTypes, 
  setCustomerTypes, 
  setCurrentView, 
  setEditingCustomerType, 
  settingsTab = 'windowTypes',
  setSettingsTab,
  isDarkMode, 
  handleThemeToggle 
}) => {
  const [editingWindow, setEditingWindow] = useState(null);
  const [tempWindowData, setTempWindowData] = useState({});
  const [syncAllEnabled, setSyncAllEnabled] = useState(JobberService.isSyncAllEnabled());
  const [syncIndividualEnabled, setSyncIndividualEnabled] = useState(JobberService.isSyncIndividualEnabled());
  const [showSyncAllConfirmation, setShowSyncAllConfirmation] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const isDraggingRef = useRef(false);
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const dragStartTimeRef = useRef(0);
  const originalDraggedIndexRef = useRef(null);
  const [displayWindowTypes, setDisplayWindowTypes] = useState(null);
  const [displayCustomerTypes, setDisplayCustomerTypes] = useState(null);

  // Listen for changes to sync preferences
  useEffect(() => {
    const handleSettingsChange = () => {
      setSyncAllEnabled(JobberService.isSyncAllEnabled());
      setSyncIndividualEnabled(JobberService.isSyncIndividualEnabled());
    };

    window.addEventListener('jobberSettingsChanged', handleSettingsChange);
    return () => window.removeEventListener('jobberSettingsChanged', handleSettingsChange);
  }, []);

  // Load sync preferences from Firestore when component mounts
  useEffect(() => {
    const loadPreferences = async () => {
      // Get userId from auth context or props if available
      // For now, we'll load from localStorage and sync to Firestore when user is available
      setSyncAllEnabled(JobberService.isSyncAllEnabled());
      setSyncIndividualEnabled(JobberService.isSyncIndividualEnabled());
    };
    loadPreferences();
  }, []);

  const addWindowType = useCallback(() => {
    try {
      // Validate inputs
      if (!setWindowTypes || typeof setWindowTypes !== 'function') {
        console.error('❌ [SettingsView] setWindowTypes is not a function!');
        return;
      }
      
      if (!Array.isArray(windowTypes)) {
        console.error('❌ [SettingsView] windowTypes is not an array!');
        return;
      }
      
      // Generate ID with fallback for older browsers
      let newId;
      try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
          newId = crypto.randomUUID();
        } else {
          throw new Error('crypto.randomUUID not available');
        }
      } catch (e) {
        newId = Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9);
      }
      
      const newType = {
        id: newId,
        name: 'New Window Type',
        price: 10,
        category: 'exterior_interior'
      };
      
      const updatedWindowTypes = [...windowTypes, newType];
      
      // Call setWindowTypes - handle both sync and async cases
      const result = setWindowTypes(updatedWindowTypes);
      if (result && typeof result.then === 'function') {
        result.catch(error => {
          console.error('❌ [SettingsView] Error in setWindowTypes:', error);
        });
      }
      
      // Update customer types
      if (Array.isArray(customerTypes) && customerTypes.length > 0) {
        const updatedCustomerTypes = customerTypes.map(customerType => ({
          ...customerType,
          windowPrices: {
            ...customerType.windowPrices,
            [newType.name]: 10
          }
        }));
        
        if (setCustomerTypes && typeof setCustomerTypes === 'function') {
          const customerResult = setCustomerTypes(updatedCustomerTypes);
          if (customerResult && typeof customerResult.then === 'function') {
            customerResult.catch(error => {
              console.error('❌ [SettingsView] Error in setCustomerTypes:', error);
            });
          }
        }
      }
      
      setEditingWindow(newType.id);
      setTempWindowData({ ...newType });
    } catch (error) {
      console.error('❌ [SettingsView] Error in addWindowType:', error);
    }
  }, [windowTypes, customerTypes, setWindowTypes, setCustomerTypes]);

  const startEditingWindow = (type) => {
    // If there's already a window being edited, save it first if there are changes
    if (editingWindow !== null && editingWindow !== type.id) {
      const currentWindow = windowTypes.find(w => w.id === editingWindow);
      if (currentWindow) {
        // Check if there are unsaved changes by comparing tempWindowData with currentWindow
        // Normalize price for comparison (handle string vs number)
        const tempPrice = tempWindowData.price === '' ? 0 : parseFloat(tempWindowData.price) || 0;
        const currentPrice = typeof currentWindow.price === 'number' ? currentWindow.price : parseFloat(currentWindow.price) || 0;
        
        const hasChanges = 
          tempWindowData.name !== currentWindow.name ||
          tempPrice !== currentPrice ||
          tempWindowData.category !== currentWindow.category;
        
        if (hasChanges) {
          // Save the current window before switching
          const finalData = {
            ...tempWindowData,
            price: tempWindowData.price === '' ? 0 : parseFloat(tempWindowData.price) || 0
          };
          
          setWindowTypes(windowTypes.map(w => 
            w.id === editingWindow ? finalData : w
          ));
        }
      }
    }
    
    // Now open the new window for editing
    setEditingWindow(type.id);
    setTempWindowData({ ...type });
  };

  const saveWindowType = (id) => {
    // Convert price string to number when saving
    const finalData = {
      ...tempWindowData,
      price: tempWindowData.price === '' ? 0 : parseFloat(tempWindowData.price) || 0
    };
    
    setWindowTypes(windowTypes.map(type => 
      type.id === id ? finalData : type
    ));
    setEditingWindow(null);
    setTempWindowData({});
  };

  const updateTempWindowData = (field, value) => {
    setTempWindowData({ ...tempWindowData, [field]: value });
  };

  const deleteWindowType = (id) => {
    setWindowTypes(windowTypes.filter(type => type.id !== id));
  };

  const handleDragStart = (e, index, isWindowTypes = false) => {
    // For window types, check if we're editing
    if (isWindowTypes && editingWindow !== null) {
      const currentWindowType = windowTypes[index];
      if (currentWindowType && editingWindow === currentWindowType.id) {
        e.preventDefault();
        return;
      }
    }
    
    // Only prevent drag if clicking directly on interactive elements (not their parent containers)
    const target = e.target;
    const isDirectInteractiveElement = target.tagName === 'INPUT' || 
                                      target.tagName === 'BUTTON' || 
                                      target.tagName === 'SELECT' ||
                                      target.tagName === 'TEXTAREA';
    
    // Also check if we're inside an interactive element
    const isInsideInteractiveElement = target.closest('input') || 
                                      target.closest('button') || 
                                      target.closest('select') ||
                                      target.closest('textarea');
    
    if (isDirectInteractiveElement || isInsideInteractiveElement) {
      e.preventDefault();
      return;
    }
    
    isDraggingRef.current = true;
    originalDraggedIndexRef.current = index;
    setDraggedItem(index);
    // Initialize display state for visual reordering during drag
    if (isWindowTypes) {
      setDisplayWindowTypes([...windowTypes]);
    } else {
      setDisplayCustomerTypes([...customerTypes]);
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', ''); // Required for some browsers
  };

  const handleDragOver = (e, index, isWindowTypes) => {
    e.preventDefault();
    
    if (originalDraggedIndexRef.current === null) return;
    if (draggedItem === index) return; // Already at this position
    
    // Update display state for visual feedback only (doesn't trigger sync)
    if (isWindowTypes) {
      setDisplayWindowTypes(currentDisplay => {
        const items = currentDisplay || windowTypes;
        const newItems = [...items];
        // Find current position of dragged item (it might have moved from original position)
        const currentDraggedIndex = draggedItem !== null ? draggedItem : originalDraggedIndexRef.current;
        const draggedItemContent = newItems[currentDraggedIndex];
        newItems.splice(currentDraggedIndex, 1);
        newItems.splice(index, 0, draggedItemContent);
        return newItems;
      });
    } else {
      setDisplayCustomerTypes(currentDisplay => {
        const items = currentDisplay || customerTypes;
        const newItems = [...items];
        // Find current position of dragged item (it might have moved from original position)
        const currentDraggedIndex = draggedItem !== null ? draggedItem : originalDraggedIndexRef.current;
        const draggedItemContent = newItems[currentDraggedIndex];
        newItems.splice(currentDraggedIndex, 1);
        newItems.splice(index, 0, draggedItemContent);
        return newItems;
      });
    }
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    // Commit the final reordered state to the actual state (triggers sync)
    if (displayWindowTypes !== null) {
      setWindowTypes(displayWindowTypes);
      setDisplayWindowTypes(null);
    }
    if (displayCustomerTypes !== null) {
      setCustomerTypes(displayCustomerTypes);
      setDisplayCustomerTypes(null);
    }
    
    // Use setTimeout to allow drag end to complete before resetting isDragging
    // This prevents onClick from firing after drag ends
    setTimeout(() => {
      isDraggingRef.current = false;
      setDraggedItem(null);
      originalDraggedIndexRef.current = null;
    }, 100);
  };

  const addCustomerType = () => {
    const windowPrices = {};
    windowTypes.forEach(windowType => {
      windowPrices[windowType.name] = windowType.price;
    });
    
    const newType = {
      id: crypto.randomUUID(), // Use UUID string to match iOS
      name: 'New Customer Type',
      windowPrices
    };
    setCustomerTypes([...customerTypes, newType]);
    setEditingCustomerType(newType);
    setCurrentView('editingCustomerType');
  };

  const editCustomerType = (customerType) => {
    setEditingCustomerType(customerType);
    setCurrentView('editingCustomerType');
  };

  const handleSyncAllChange = async (enabled) => {
    console.log('🔄 Sync All toggle changed:', {
      newValue: enabled,
      oldValue: syncAllEnabled
    });
    
    await JobberService.setSyncAllEnabled(enabled, null, true); // Skip confirmation since UI handles it
    setSyncAllEnabled(enabled);
    
    // Notify other components
    window.dispatchEvent(new CustomEvent('jobberSettingsChanged'));
    
    console.log('✅ Sync All setting updated:', {
      newValue: enabled,
      localStorageValue: localStorage.getItem(`jobber_sync_all_${JobberService.getPlatform()}`)
    });
  };

  const handleSyncIndividualChange = async (enabled) => {
    console.log('🔄 Sync Individual toggle changed:', {
      newValue: enabled,
      oldValue: syncIndividualEnabled
    });
    
    await JobberService.setSyncIndividualEnabled(enabled, null);
    setSyncIndividualEnabled(enabled);
    
    // Notify other components
    window.dispatchEvent(new CustomEvent('jobberSettingsChanged'));
    
    console.log('✅ Sync Individual setting updated:', {
      newValue: enabled,
      localStorageValue: localStorage.getItem(`jobber_sync_individual_${JobberService.getPlatform()}`)
    });
  };

  return (
    <div className="min-h-screen bg-primary">
      <div className="header shadow-lg border-b border-primary sticky top-0 z-10 relative">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => setCurrentView('quotes')} className="flex items-center text-accent-primary hover:text-accent-hover">
            <X className="w-5 h-5 mr-2" />
            Back
          </button>
          <div className="w-16"></div>
        </div>
        <h1 className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-xl font-bold text-primary m-0">Settings</h1>
      </div>

      {/* Mobile Tabs - Horizontal Small Buttons */}
      <div className="block md:hidden w-full border-b border-primary p-3">
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setSettingsTab('windowTypes')}
            className={`btn flex items-center gap-2 py-2 px-4 ${
              settingsTab === 'windowTypes' 
                ? 'btn-primary' 
                : 'btn-secondary'
            }`}
          >
            <Grid3X3 className="w-4 h-4" />
            <span className="text-sm font-semibold">Windows</span>
          </button>
          <button
            onClick={() => setSettingsTab('customerTypes')}
            className={`btn flex items-center gap-2 py-2 px-4 ${
              settingsTab === 'customerTypes' 
                ? 'btn-primary' 
                : 'btn-secondary'
            }`}
          >
            <Home className="w-4 h-4" />
            <span className="text-sm font-semibold">Customers</span>
          </button>
          <button
            onClick={() => setSettingsTab('appearance')}
            className={`btn flex items-center gap-2 py-2 px-4 ${
              settingsTab === 'appearance' 
                ? 'btn-primary' 
                : 'btn-secondary'
            }`}
          >
            <Settings className="w-4 h-4" />
            <span className="text-sm font-semibold">Settings</span>
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Desktop Tabs - Text Only */}
        <div className="hidden md:flex flex-col w-64 border-r border-primary min-h-screen p-2 space-y-2">
          <button
            onClick={() => setSettingsTab('windowTypes')}
            className={`btn px-4 py-4 text-sm font-semibold rounded-xl text-left ${
              settingsTab === 'windowTypes' 
                ? 'btn-primary' 
                : 'btn-secondary'
            }`}
          >
            Window Types
          </button>
          <button
            onClick={() => setSettingsTab('customerTypes')}
            className={`btn px-4 py-4 text-sm font-semibold rounded-xl text-left ${
              settingsTab === 'customerTypes' 
                ? 'btn-primary' 
                : 'btn-secondary'
            }`}
          >
            Customer Types
          </button>
          <button
            onClick={() => setSettingsTab('appearance')}
            className={`btn px-4 py-4 text-sm font-semibold rounded-xl text-left ${
              settingsTab === 'appearance' 
                ? 'btn-primary' 
                : 'btn-secondary'
            }`}
          >
            App Settings
          </button>
        </div>
        
        <div className="flex-1 p-3 md:p-6 settings-content">
          {settingsTab === 'windowTypes' && (
          <div className="space-y-4 window-types-container">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-primary">Window Types</h2>
              <button
                onClick={addWindowType}
                className="btn btn-primary"
                type="button"
              >
                <Plus className="w-5 h-5 mr-2 inline" />
                Add Window Type
              </button>
            </div>
            
            {(displayWindowTypes !== null ? displayWindowTypes : windowTypes).map((type, index) => {
              const isDragged = draggedItem === index;
              const isEditing = editingWindow === type.id;
              
              return (
              <div 
                key={type.id} 
                draggable={!isEditing}
                onDragStart={(e) => handleDragStart(e, index, true)}
                onDragOver={(e) => handleDragOver(e, index, true)}
                onDragEnd={handleDragEnd}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onMouseDown={(e) => {
                  // Store initial mouse position and time to detect drag vs click
                  dragStartPosRef.current = { x: e.clientX, y: e.clientY };
                  dragStartTimeRef.current = Date.now();
                  isDraggingRef.current = false;
                }}
                onClick={(e) => {
                  // Don't trigger click if we just finished dragging or are currently dragging
                  if (isDraggingRef.current || draggedItem !== null) {
                    return;
                  }
                  // Check if mouse moved significantly (indicating a drag) or if too much time passed
                  const timeSinceMouseDown = Date.now() - dragStartTimeRef.current;
                  const moved = Math.abs(e.clientX - dragStartPosRef.current.x) > 5 || 
                               Math.abs(e.clientY - dragStartPosRef.current.y) > 5;
                  // If mouse moved significantly or too much time passed, it was likely a drag
                  if (!moved && timeSinceMouseDown < 300 && !isEditing) {
                    startEditingWindow(type);
                  }
                }}
                className={`draggable-item card cursor-pointer hover:bg-quaternary window-type-card transition-all duration-200 ease-out`}
                style={{ 
                  cursor: isEditing ? 'default' : (isDragged ? 'grabbing' : 'grab'),
                  opacity: isDragged ? 0.5 : 1,
                  border: hoveredIndex === index && !isDragged && !isEditing ? '1px solid var(--accent-primary)' : '1px solid transparent',
                  boxShadow: hoveredIndex === index && !isDragged ? '0 8px 25px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s, box-shadow 0.2s, opacity 0.2s, border-color 0.2s',
                  transform: hoveredIndex === index && !isDragged ? 'translateY(-2px)' : 'translateY(0)',
                  userSelect: 'none',
                  WebkitUserSelect: 'none'
                }}
              >
                {editingWindow === type.id ? (
                  <div className="window-type-edit-form">
                    {/* Row 1: Window Name and Cost */}
                    <div className="window-type-edit-row">
                      <input
                        type="text"
                        value={tempWindowData.name || ''}
                        onChange={(e) => updateTempWindowData('name', e.target.value)}
                        className="input-field focusable text-lg font-bold window-type-name-input"
                        placeholder="Window Type Name"
                      />
                      <div className="window-type-cost-wrapper">
                        <span className="cost-dollar-sign">$</span>
                        <input
                          type="text"
                          value={tempWindowData.price || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow empty string, numbers, and decimal points
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              updateTempWindowData('price', value);
                            }
                          }}
                          onFocus={(e) => {
                            const input = e.target;
                            const length = input.value.length;
                            input.setSelectionRange(length, length);
                          }}
                          onClick={(e) => {
                            const input = e.target;
                            const length = input.value.length;
                            input.setSelectionRange(length, length);
                          }}
                          className="input-field text-right focusable window-type-cost-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    
                    {/* Row 2: Category Dropdown, Delete, Save */}
                    <div className="window-type-edit-row window-type-edit-row-buttons">
                      <select
                        value={tempWindowData.category || 'exterior_interior'}
                        onChange={(e) => updateTempWindowData('category', e.target.value)}
                        className="form-select window-type-category-select"
                        title="Type"
                      >
                        <option value="exterior_interior">Exterior/Interior</option>
                        <option value="separate">Separate Item</option>
                      </select>
                      <div className="window-type-buttons-group">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteWindowType(type.id);
                          }}
                          className="edit-btn-wide bg-danger text-white window-type-delete-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            saveWindowType(type.id);
                          }}
                          className="edit-btn-wide btn-primary window-type-save-btn"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <GripVertical 
                      className="w-5 h-5 text-tertiary flex-shrink-0 drag-handle" 
                      style={{ opacity: 0.5, cursor: 'grab', pointerEvents: 'auto' }}
                    />
                    <div className="flex-1">
                      <div className="font-bold text-lg text-primary">{type.name}</div>
                      <div className="text-sm text-tertiary">
                        {type.category === 'exterior_interior' ? 'Exterior/Interior' : 'Separate Item'}
                      </div>
                    </div>
                    <div className="text-xl font-bold text-primary">${type.price}</div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}

        {settingsTab === 'customerTypes' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-primary">Customer Types</h2>
              <button
                onClick={addCustomerType}
                className="btn btn-primary"
              >
                <Plus className="w-5 h-5 mr-2 inline" />
                Add Customer Type
              </button>
            </div>
            
            {(displayCustomerTypes !== null ? displayCustomerTypes : customerTypes).map((type, index) => {
              const isDragged = draggedItem === index;
              
              return (
              <div 
                key={type.id} 
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index, false)}
                onDragEnd={handleDragEnd}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={(e) => {
                  if (draggedItem === null) {
                    editCustomerType(type);
                  }
                }}
                className={`draggable-item card cursor-pointer hover:bg-quaternary transition-all duration-200 ease-out`}
                style={{ 
                  cursor: isDragged ? 'grabbing' : 'grab',
                  opacity: isDragged ? 0.5 : 1,
                  border: hoveredIndex === index && !isDragged ? '1px solid var(--accent-primary)' : '1px solid transparent',
                  boxShadow: hoveredIndex === index && !isDragged ? '0 8px 25px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s, box-shadow 0.2s, opacity 0.2s, border-color 0.2s',
                  transform: hoveredIndex === index && !isDragged ? 'translateY(-2px)' : 'translateY(0)',
                  userSelect: 'none'
                }}
              >
                <div className="flex items-center gap-4">
                  <GripVertical className="w-5 h-5 text-tertiary flex-shrink-0 drag-handle" style={{ opacity: 0.5, cursor: 'grab', pointerEvents: 'auto' }} />
                  <div className="flex-1">
                    <div className="font-bold text-lg text-primary">{type.name}</div>
                    <div className="text-sm text-tertiary">
                      {Object.keys(type.windowPrices).length} window types configured
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {settingsTab === 'appearance' && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-primary">App Settings</h2>
            {console.log('Rendering App Settings tab with Jobber integration')}
            
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold text-lg text-primary">Dark Mode</div>
                  <div className="text-sm text-tertiary">
                    Switch between light and dark themes
                  </div>
                </div>
                <div className="relative rounded-full p-1 flex items-center" style={{ backgroundColor: 'var(--bg-quaternary)' }}>
                  <div className={`absolute top-1 bottom-1 rounded-full transition-all duration-300 ${
                    isDarkMode 
                      ? 'left-1/2 right-1' 
                      : 'left-1 right-1/2'
                  }`} style={{ 
                    background: isDarkMode 
                      ? 'linear-gradient(to right, var(--bg-tertiary), var(--bg-secondary))' 
                      : 'linear-gradient(to right, var(--bg-secondary), var(--bg-quaternary))'
                  }} />
                  <button
                    onClick={() => handleThemeToggle(false)}
                    className={`relative z-10 px-4 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                      !isDarkMode 
                        ? 'text-lg transform scale-110' 
                        : 'text-sm'
                    }`}
                    style={{ 
                      color: !isDarkMode ? 'var(--text-primary)' : 'var(--text-tertiary)' 
                    }}
                  >
                    ☀️
                  </button>
                  <button
                    onClick={() => handleThemeToggle(true)}
                    className={`relative z-10 px-4 py-2 rounded-full text-sm font-bold transition-all duration-300 ${
                      isDarkMode 
                        ? 'text-lg transform scale-110' 
                        : 'text-sm'
                    }`}
                    style={{ 
                      color: isDarkMode ? 'var(--text-primary)' : 'var(--text-tertiary)' 
                    }}
                  >
                    🌙
                  </button>
                </div>
              </div>
            </div>
            
            {/* Jobber Integration */}
            <div className="card">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {JobberService.isAuthenticated() && (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" aria-hidden />
                    )}
                    <div>
                      <div className="font-bold text-lg text-primary">Jobber Integration</div>
                      <div className="text-sm text-tertiary">
                        {JobberService.isAuthenticated() 
                          ? 'Connected to Jobber - Quotes can be synced automatically'
                          : 'Not connected - Connect to Jobber to sync quotes and manage customers'
                        }
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      console.log('Jobber connect button clicked');
                      window.dispatchEvent(new CustomEvent('openJobberIntegration'));
                    }}
                    className="btn btn-primary flex items-center justify-center w-full sm:w-auto"
                  >
                    {JobberService.isAuthenticated() ? 'Manage' : 'Connect'}
                  </button>
                </div>
                
                {/* Sync Preferences - Only show if connected */}
                {JobberService.isAuthenticated() && (
                  <div className="space-y-4 pt-4 border-t border-primary">
                    {/* Sync All Quotes Toggle */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-primary">Sync All Quotes</div>
                        <div className="text-sm text-tertiary">
                          Automatically sync all quotes to Jobber when saved
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={syncAllEnabled}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            if (newValue) {
                              // Show confirmation dialog when enabling
                              setShowSyncAllConfirmation(true);
                            } else {
                              // No confirmation needed when disabling
                              handleSyncAllChange(false);
                            }
                          }}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 rounded-full transition-colors duration-300 ${
                          syncAllEnabled ? 'bg-accent-primary' : ''
                        }`} style={{
                          backgroundColor: syncAllEnabled ? 'var(--accent-primary)' : 'var(--bg-quaternary)'
                        }}>
                          <div className={`absolute top-[2px] left-[2px] rounded-full h-5 w-5 transition-transform duration-300 ${
                            syncAllEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`} style={{
                            backgroundColor: 'var(--bg-secondary)',
                            border: '1px solid var(--border-primary)'
                          }}></div>
                        </div>
                      </label>
                    </div>

                    {/* Sync Individual Quotes Toggle */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-primary">Sync Individual Quotes</div>
                        <div className="text-sm text-tertiary">
                          Enable manual sync for individual quotes
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={syncIndividualEnabled}
                          onChange={(e) => {
                            handleSyncIndividualChange(e.target.checked);
                          }}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 rounded-full transition-colors duration-300 ${
                          syncIndividualEnabled ? 'bg-accent-primary' : ''
                        }`} style={{
                          backgroundColor: syncIndividualEnabled ? 'var(--accent-primary)' : 'var(--bg-quaternary)'
                        }}>
                          <div className={`absolute top-[2px] left-[2px] rounded-full h-5 w-5 transition-transform duration-300 ${
                            syncIndividualEnabled ? 'translate-x-5' : 'translate-x-0'
                          }`} style={{
                            backgroundColor: 'var(--bg-secondary)',
                            border: '1px solid var(--border-primary)'
                          }}></div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Confirmation Dialog */}
                {showSyncAllConfirmation && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-secondary rounded-2xl p-6 shadow-lg border border-primary max-w-md">
                      <h3 className="text-xl font-bold text-primary mb-4">Enable Sync All Quotes?</h3>
                      <p className="text-sm text-tertiary mb-6">
                        This will automatically sync all quotes to Jobber when saved. You can still sync individual quotes manually.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            setShowSyncAllConfirmation(false);
                          }}
                          className="btn btn-secondary flex-1"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            handleSyncAllChange(true);
                            setShowSyncAllConfirmation(false);
                          }}
                          className="btn btn-primary flex-1"
                        >
                          Enable
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default SettingsView;
